/**
 * SMS Service - Twilio Integration
 * Sends SMS notifications to clients for emergency booking updates
 */

import { auth } from '../config/firebase';

// Twilio credentials - stored in environment variables or Firebase config
interface TwilioConfig {
  accountSid: string;
  apiKeySid: string;    // API Key SID (SKxxx) for authentication
  apiKeySecret: string; // API Key Secret for authentication
  phoneNumber: string;
}

interface SMSMessage {
  to: string;
  body: string;
}

// SMS now sends server-side via the sendSms Cloud Function — Twilio creds live ONLY in
// functions config (the old btoa(apiKeySecret) path shipped the secret in the browser bundle,
// which is why SMS stayed dormant). Jose sets creds with scripts/set-twilio.sh, then flips
// REACT_APP_SMS_ENABLED=true. (Jose 2026-08-22)
const SMS_FUNCTIONS_URL = 'https://us-central1-slik-pos-barberlegacy.cloudfunctions.net';

class SMSService {
  private config: TwilioConfig | null = null;

  /**
   * Initialize with Twilio credentials
   */
  initialize(config: TwilioConfig): void {
    this.config = config;
    console.log('📱 SMS Service initialized with Twilio');
  }

  /**
   * Check if service is configured
   */
  isConfigured(): boolean {
    // Server-side send now: creds live in the sendSms Cloud Function, never the app bundle.
    // Jose flips REACT_APP_SMS_ENABLED=true in .env once Twilio is wired + we redeploy.
    return process.env.REACT_APP_SMS_ENABLED === 'true';
  }

  /**
   * Send SMS via the sendSms Cloud Function (Twilio creds server-side).
   */
  async sendSMS(message: SMSMessage): Promise<{ success: boolean; error?: string }> {
    // Format phone number (ensure it has country code)
    const formattedPhone = this.formatPhoneNumber(message.to);
    if (!formattedPhone) {
      return { success: false, error: 'Invalid phone number' };
    }

    // 🛑 STAGING / DISABLED GUARD (Jose 2026-10-29): NEVER send a real text from a STAGING build or when SMS
    // is disabled. This method calls the PROD sendSms Cloud Function by hardcoded URL regardless of which
    // env the app runs in — so a real client got a real text while we were testing on staging. Gate on
    // REACT_APP_SMS_ENABLED (true only in prod) + an explicit staging-env block. Simulate success so the UI
    // flow (toasts, thread logging) still works during testing; nothing actually leaves the building.
    if (process.env.REACT_APP_ENVIRONMENT === 'staging' || process.env.REACT_APP_SMS_ENABLED !== 'true') {
      console.log('📵 [SMS suppressed — staging/disabled]', formattedPhone, '·', (message.body || '').slice(0, 48));
      return { success: true };
    }

    // Attach the signed-in staff member's ID token so the gated sendSms endpoint accepts it. (2026-10-13)
    const idToken = await auth.currentUser?.getIdToken().catch(() => undefined);
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) };
    const payload = JSON.stringify({ to: formattedPhone, body: message.body });

    // RESILIENT SEND (Jose 2026-10-30): the shop WiFi drops the occasional fetch to cloudfunctions.net
    // ("TypeError: Load failed") — the waitlist "Message via SLIK Connect" was dying on a transient blip.
    // Retry a NETWORK failure up to 3× with backoff; a real HTTP error (403/400/500) is NOT retried — it's
    // surfaced. (The durable fix — queue outbound to Firestore + a sender CF — is part of SLIK Connect.)
    let lastNetErr: any = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await fetch(`${SMS_FUNCTIONS_URL}/sendSms`, { method: 'POST', headers, body: payload });
        const data = await response.json().catch(() => ({} as any));
        if (response.ok && data.success) {
          console.log('✅ SMS sent successfully:', data.sid);
          return { success: true };
        }
        console.error('❌ SMS failed:', data.message || data.error, `(HTTP ${response.status})`);
        return { success: false, error: data.message || data.error || `SMS send failed (${response.status})` };
      } catch (error) {
        lastNetErr = error; // network-level failure — retry
        if (attempt < 2) await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
    }
    console.error('❌ SMS network error after retries:', lastNetErr);
    return { success: false, error: 'Network error — message not sent. Check the connection and try again.' };
  }

  /**
   * Format phone number to E.164 format
   */
  private formatPhoneNumber(phone: string): string | null {
    if (!phone) return null;

    // Remove all non-numeric characters
    let cleaned = phone.replace(/\D/g, '');

    // Add US country code if not present
    if (cleaned.length === 10) {
      cleaned = '1' + cleaned;
    }

    // Validate length
    if (cleaned.length < 10 || cleaned.length > 15) {
      return null;
    }

    return '+' + cleaned;
  }

  /**
   * Send counter-offer notification to client
   */
  async sendCounterOfferNotification(params: {
    clientPhone: string;
    clientName: string;
    proposedDate: string;
    proposedTime: string;
    barberName: string;
    message?: string;
    dashboardUrl: string;
  }): Promise<{ success: boolean; error?: string }> {
    const { clientPhone } = params;
    return this.sendSMS({ to: clientPhone, body: buildCounterOfferBody(params) });
  }

  /**
   * Send booking confirmation SMS
   */
  async sendBookingConfirmation(params: {
    clientPhone: string;
    clientName: string;
    date: string;
    time: string;
    barberName: string;
    service: string;
    manageUrl?: string;
  }): Promise<{ success: boolean; error?: string }> {
    const { clientPhone, clientName, date, time, barberName, service, manageUrl } = params;

    const smsBody = `Hi ${clientName}! ✅

Your SLIK emergency appointment is confirmed:

📅 ${date}
🕐 ${time}
💇 ${service}
👤 with ${barberName}
${manageUrl ? `\nManage your appointment:\n${manageUrl}` : ''}
See you soon!

Reply STOP to opt out.`;

    return this.sendSMS({
      to: clientPhone,
      body: smsBody
    });
  }
  /**
   * Send "On My Way" notification to staff (optional — for shops that want SMS alerts)
   */
  async sendOnMyWayNotification(params: {
    staffPhone: string;
    clientName: string;
    eta: number;
    appointmentTime: string;
  }): Promise<{ success: boolean; error?: string }> {
    const { staffPhone, clientName, eta, appointmentTime } = params;

    return this.sendSMS({
      to: staffPhone,
      body: `📍 ${clientName} is on the way! ETA: ${eta} min (appointment at ${appointmentTime})`
    });
  }

  /**
   * Send "Running Late" notification to staff
   */
  async sendRunningLateNotification(params: {
    staffPhone: string;
    clientName: string;
    additionalMinutes: number;
    appointmentTime: string;
  }): Promise<{ success: boolean; error?: string }> {
    const { staffPhone, clientName, additionalMinutes, appointmentTime } = params;

    return this.sendSMS({
      to: staffPhone,
      body: `⏰ ${clientName} is running +${additionalMinutes} min late for their ${appointmentTime} appointment`
    });
  }

  /**
   * Send reschedule notification to client
   */
  async sendRescheduleNotification(params: {
    clientPhone: string;
    clientName: string;
    newTime: string;
  }): Promise<{ success: boolean; error?: string }> {
    const { clientPhone, clientName, newTime } = params;

    return this.sendSMS({
      to: clientPhone,
      body: `Hi ${clientName}! Your SLIK appointment has been rescheduled to ${newTime}. See you then! Reply STOP to opt out.`
    });
  }
  /**
   * Send manager fraud alert when a flagged discount checkout completes
   */
  async sendManagerFraudAlert(params: {
    managerPhone: string;
    managerName: string;
    barberName: string;
    clientName: string;
    originalPrice: number;
    discountAmount: number;
    finalPrice: number;
    paymentMethod: string;
    riskScore: number;
    severity: string;
    fraudFlags: string[];
    discountReason: string;
  }): Promise<{ success: boolean; error?: string }> {
    const { managerPhone, barberName, clientName, originalPrice, discountAmount, finalPrice, paymentMethod, riskScore, severity, fraudFlags, discountReason } = params;

    const flagsText = fraudFlags.slice(0, 3).map(f => f.replace(/_/g, ' ')).join(', ');

    const smsBody = `SLIK ALERT: Flagged discount checkout

Barber: ${barberName}
Client: ${clientName}
Original: $${originalPrice.toFixed(2)}
Discount: -$${discountAmount.toFixed(2)}
Charged: $${finalPrice.toFixed(2)}
Payment: ${paymentMethod.toUpperCase()}
Risk: ${severity.toUpperCase()} (${riskScore}/100)
Reason: ${discountReason}
Flags: ${flagsText}

Review in SLIK Manager Dashboard.`;

    return this.sendSMS({
      to: managerPhone,
      body: smsBody
    });
  }

  /**
   * Send client checkout receipt/thank-you SMS
   * Important: Shows original price + discount so client sees what was actually charged
   */
  async sendCheckoutReceipt(params: {
    clientPhone: string;
    clientName: string;
    barberName: string;
    serviceName: string;
    originalPrice: number;
    discountAmount: number;
    tipAmount: number;
    totalCharged: number;
    paymentMethod: string;
    shopName?: string;
  }): Promise<{ success: boolean; error?: string }> {
    return this.sendSMS({ to: params.clientPhone, body: buildCheckoutReceiptBody(params) });
  }
}

/**
 * Pure builder for the checkout-receipt SMS body. Extracted so the SEND can go through SLIK Connect's
 * durable queue (communicationService.sendToClient → source 'checkout_receipt'), which threads + labels the
 * receipt and survives a WiFi blip — instead of the old direct Twilio fetch. (Jose 2026-10-30)
 */
export function buildCheckoutReceiptBody(params: {
  clientName: string; barberName: string; serviceName: string;
  originalPrice: number; discountAmount: number; tipAmount: number; totalCharged: number; shopName?: string;
}): string {
  const { clientName, barberName, serviceName, originalPrice, discountAmount, tipAmount, totalCharged, shopName } = params;
  let smsBody = `Thanks for visiting${shopName ? ` ${shopName}` : ''}, ${clientName}!

${serviceName} with ${barberName}
Service: $${originalPrice.toFixed(2)}`;
  if (discountAmount > 0) smsBody += `\nDiscount: -$${discountAmount.toFixed(2)}`;
  if (tipAmount > 0) smsBody += `\nTip: $${tipAmount.toFixed(2)}`;
  smsBody += `\nTotal: $${totalCharged.toFixed(2)}

We'd love your feedback! Reply 1-5 to rate your experience.

Reply STOP to opt out.`;
  return smsBody;
}

/** Pure builder for the emergency counter-offer SMS body — routed through the Connect queue (source 'emergency'). */
export function buildCounterOfferBody(params: {
  clientName: string; proposedDate: string; proposedTime: string; barberName: string; message?: string; dashboardUrl: string;
}): string {
  const { clientName, proposedDate, proposedTime, barberName, message, dashboardUrl } = params;
  return `Hi ${clientName}! 💈

SLIK Barber has proposed a new time for your emergency booking:

📅 ${proposedDate}
🕐 ${proposedTime}
💇 with ${barberName}
${message ? `\n"${message}"` : ''}

Accept or decline here:
${dashboardUrl}

Reply STOP to opt out.`;
}

// Export singleton instance
export const smsService = new SMSService();

// Export config type for initialization
export type { TwilioConfig };
